/**
 * Custom hooks for vibesites components
 */

import { useMemo } from 'react';
import type { ComponentType } from 'react';
import type { ComponentSlot } from '../types';
import type { Floorplan, SpecialOffer } from '@vibesites/schema';
import { useProperty, useRegistry, useTokens } from './context';
import { token as tokenHelper } from '@vibesites/tokens';
import { getPropertyAddressUrl } from '../property-address-url';
import { useLiveCollection } from './use-live-collection';

// ============================================================================
// Component Resolution Hooks
// ============================================================================

/**
 * Get a component from the registry by slot name.
 */
export function useComponent<P = unknown>(slot: ComponentSlot): ComponentType<P> | null {
  const registry = useRegistry();
  return registry[slot] as ComponentType<P> | null;
}

/**
 * Check if a component slot is available.
 */
export function useHasComponent(slot: ComponentSlot): boolean {
  const registry = useRegistry();
  return registry[slot] !== null && registry[slot] !== undefined;
}

// ============================================================================
// Property Data Hooks
// ============================================================================

/**
 * Get a specific property field with type safety.
 */
export function usePropertyField<K extends keyof ReturnType<typeof useProperty>>(
  field: K
): ReturnType<typeof useProperty>[K] {
  const property = useProperty();
  return property[field];
}

/**
 * Get property name.
 */
export function usePropertyName(): string {
  return usePropertyField('property_name') || '';
}

/**
 * Get the public-facing property phone number.
 *
 * Prefers `property_call_tracking_phone` so attribution works end-to-end,
 * and falls back to the canonical `property_phone` when no tracking line
 * is configured. Consumers that need the raw number (e.g. JSON-LD / SEO
 * structured data) should read `property.property_phone` directly.
 */
export function usePropertyPhone(): string {
  return (
    usePropertyField('property_call_tracking_phone') ||
    usePropertyField('property_phone') ||
    ''
  );
}

/**
 * Get property address.
 */
export function usePropertyAddress(): string {
  return usePropertyField('property_full_address') || '';
}

/**
 * Get a mobile-safe Google Maps address URL.
 */
export function usePropertyAddressUrl(): string {
  return getPropertyAddressUrl(useProperty()) || '';
}

/**
 * Get social media URLs.
 */
export function useSocialLinks() {
  const property = useProperty();
  
  return useMemo(() => ({
    facebook: property.property_facebook_url,
    instagram: property.property_instagram_url,
    twitter: property.property_twitter_url,
    linkedin: property.property_linkedin_url,
    youtube: property.property_youtube_url,
    tiktok: property.property_tiktok_url,
    yelp: property.property_yelp_url,
  }), [property]);
}

/**
 * Get action URLs (apply, resident portal, etc.)
 */
export function useActionUrls() {
  const property = useProperty();
  
  return useMemo(() => ({
    applyNow: property.property_apply_now_url,
    residentPortal: property.property_resident_portal_url,
    onlineLeasing: property.property_online_leasing_url,
    apartments: property.property_apartments_url,
  }), [property]);
}

// ============================================================================
// Token Hooks
// ============================================================================

/**
 * Get a CSS variable reference for a token path.
 */
export function useTokenVar(path: string, fallback?: string): string {
  return tokenHelper.var(path, fallback);
}

/**
 * Get the primary color token.
 */
export function usePrimaryColor(): string {
  const tokens = useTokens();
  return tokens.color.primary;
}

/**
 * Get the secondary color token.
 */
export function useSecondaryColor(): string {
  const tokens = useTokens();
  return tokens.color.secondary;
}

// ============================================================================
// Collection Hooks
// ============================================================================

/**
 * Get amenities with optional filtering.
 */
export function useAmenities(options?: { featured?: boolean; group?: string; limit?: number }) {
  const property = useProperty();
  
  return useMemo(() => {
    let amenities = property.amenities || [];
    
    if (options?.featured) {
      amenities = amenities.filter(a => a.is_featured === 'true');
    }
    
    if (options?.group) {
      amenities = amenities.filter(a => a.group === options.group);
    }
    
    if (options?.limit) {
      amenities = amenities.slice(0, options.limit);
    }
    
    return amenities;
  }, [property.amenities, options?.featured, options?.group, options?.limit]);
}

/**
 * Get floorplans. Prefers live data from the edge worker when available,
 * falling back to baked build-time data.
 */
export function useFloorplans() {
  const property = useProperty();
  const live = useLiveCollection<Floorplan>('floorplans');
  return live ?? property.floorplans ?? [];
}

/**
 * Get virtual tours.
 */
export function useVirtualTours() {
  const property = useProperty();
  return property.virtual_tours || [];
}

/**
 * Get photo gallery.
 */
export function useGallery() {
  const property = useProperty();
  return property.photo_gallery || [];
}

/**
 * Get office hours.
 */
export function useOfficeHours() {
  const property = useProperty();
  return property.office_hours || [];
}

/**
 * Get neighborhood POIs with optional type filtering.
 */
export function useNeighborhoodPOIs(type?: string) {
  const property = useProperty();
  
  return useMemo(() => {
    let pois = property.neighborhood_pois || [];
    
    if (type) {
      pois = pois.filter(poi => poi.poi_type === type);
    }
    
    return pois;
  }, [property.neighborhood_pois, type]);
}

/**
 * Get reviews.
 */
export function useReviews() {
  const property = useProperty();
  return property.reviews || [];
}

/**
 * Get FAQs with optional category filtering.
 */
export function useFaqs(category?: string) {
  const property = useProperty();

  return useMemo(() => {
    let faqs = property.faqs || [];

    if (category) {
      faqs = faqs.filter(faq => faq.category === category);
    }

    return faqs;
  }, [property.faqs, category]);
}

/**
 * Get special offers (active ones only by default). Prefers live data
 * from the edge worker when available, falling back to baked build-time data.
 */
export function useSpecialOffers(includeInactive = false) {
  const property = useProperty();
  const live = useLiveCollection<SpecialOffer>('special_offers');
  const source = live ?? property.special_offers ?? [];

  return useMemo(() => {
    if (includeInactive) return source;
    return source.filter(o => o.enabled === 'true');
  }, [source, includeInactive]);
}

